import { Col, Row } from "antd";
import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "../../../../../../contexts/AuthContext";
import query from "../../../../../../server/constants.api";
import logger from "../../../../../../server/server";
import useFetch from "../../../../../../server/useFetch";
import { PageContent } from "../../../../../../styles/global/Pages.styled";
import Theme from "../../../../../../styles/Theme/Theme";
import { metrics, months, years } from "../../../../../../utils/dummydata"; // Definindo meses e anos para os dropdowns de seleção
import { orbitCamera } from "../../../../../../utils/renders/render-helper";
import { SelectAtomic } from "../../../../Atomics/SelectAtomic";
import { Widget } from "../../../../Atomics/Widget";
import { FloorVacancyComponent } from "../../../FloorVacancyComponent/FloorVacancyComponent";
import { StackedFloorsComponent } from "../../../StackedFloorsComponent/StackedFloorsComponent";

// Componente principal que exibe o histórico de consumo energético de um edifício
export const BuildingConfortHistoric = ({
  building,
  currentFloor,
  ...props
}) => {
  // Hook de tradução
  const { t } = useTranslation();
  const { user, tenant } = useAuthContext();

  useEffect(() => {
    logger.info("User: " + user?.Username + " entered BuildingConfortHistoric successfully! Tenant: " + tenant);
  }, []);

  // Definindo o mês e ano atuais
  const currentMonth = new Date().getMonth() + 1; // JavaScript getMonth() retorna o mês de 0 a 11, então adicionamos 1
  const [selectedMonth, setSelectedMonth] = useState(
    months(t).find((month) => month.value === currentMonth)?.value
  );
  const [selectedYear, setSelectedYear] = useState(2024);
  const [selectedMetrics, setselectedMetrics] = useState('temperature');
  const [orbitCameraVal, setOrbitCamera] = useState(false);
  const [orbitControls, setOrbitControls] = useState();

  function toggleCamera() {
    orbitCamera(orbitControls, !orbitCameraVal);
    setOrbitCamera(!orbitCameraVal);
  }

  const { data: confortHistoricData, isFetching: confortHistoricDataIsFetching, refetch: refetchConfortHistoricData } = useFetch(
    "/registry/comfort/historic", "confortHistoricData",
    {
      tenant: building?.tenant,
      year: selectedYear
    }
    , currentFloor?.value ?? false, null, query.middlewareURL);

  useEffect(() => {
    if (!(confortHistoricData == null || confortHistoricData == undefined))
      if (Object.keys(confortHistoricData).length === 0) {
        logger.warn("User: " + user?.Username + " Tenant: " + tenant + ", confortHistoricData is a empty object!");
      }
  }, [confortHistoricData]);

  // Refetch dos dados quando o mês ou ano selecionados mudam
  useEffect(() => {
    refetchConfortHistoricData();
    setOrbitCamera(false);
  }, [selectedYear]);

  return (
    <>
      {/* Conteúdo da página com layout e estrutura com Col e Rows */}
      <PageContent className={clsx("building_room_details", props?.className)}>
        <Row gutter={[16, 16]}>
          {/* Define o layout de linhas com largura total */}
          <Col span={24} style={{ alignContent: "center" }}>
            <Row>
              <Col span={10}>
                <Row>
                  <Col span={24}>
                    <h3>
                      {building?.name} | {t("Comfort").toLocaleUpperCase()}{" "}
                      {/* Changes the title to upperCase and translates it */}
                    </h3>
                  </Col>
                </Row>
              </Col>
              <Col span={8} offset={6} style={{ alignContent: "center" }}>
                <Row gutter={16}>
                  <Col span={8}>
                    <SelectAtomic
                      optionsData={metrics.map(x => ({ label: t(x.label), value: x.value }))}
                      handleOptionsChange={(value) => {
                        setselectedMetrics(value);
                      }}
                      defaultValue={selectedMetrics}
                      fullWidth={true}
                    />
                  </Col>
                  <Col span={8}>
                    <SelectAtomic
                      optionsData={months(t)}
                      handleOptionsChange={(value) => {
                        setSelectedMonth(value);
                      }}
                      defaultValue={selectedMonth}
                      fullWidth={true}
                    />
                  </Col>
                  <Col span={8}>
                    <SelectAtomic
                      optionsData={years}
                      handleOptionsChange={(value) => {
                        setSelectedYear(value);
                      }}
                      defaultValue={selectedYear}
                      fullWidth={true}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>

          {/* Widgets com dados de consumo energético */}
          <Col span={24}>
            <Row gutter={[16, 16]}>
              <Col span={10}>
                <Widget
                  className="full-h"
                  data={{
                    background: "white",
                    title: t("Heatmap"),
                    icon: null,
                    buttons: [{ key: t("360"), title: t("360º"), onButtonClick: () => toggleCamera(), isActive: orbitCameraVal }]
                  }}
                >
                  <div style={{ height: "480px" }}>
                    {currentFloor?.value === building?.id ? <StackedFloorsComponent building={building} metric={selectedMetrics} setOrbitControls={setOrbitControls} /> :
                      <FloorVacancyComponent building={building} currentFloor={currentFloor} metric={selectedMetrics} setOrbitControls={setOrbitControls} />}
                  </div>
                </Widget>
              </Col>
              <Col span={14}>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Widget
                      className="full-h"
                      data={{
                        background: "white", // Define o fundo como transparente
                        title: t("Average Value"), // Título do Widget traduzido
                        icon: null, // Nenhum ícone definido
                        charts: [
                          {
                            chartType: "bigNumberChart",
                            chartId: "bigNumberChartOne",
                            height: "111px",
                            isFetching: confortHistoricDataIsFetching,
                            data: confortHistoricData ? {
                              "number": getAverageOfThatMonth(confortHistoricData[selectedMetrics][currentFloor.label], selectedYear, selectedMonth),
                              "unit": confortHistoricData[selectedMetrics][currentFloor.label].unit,
                              "type": null,
                              "label": null,
                              "fluctuation": null,
                              "fluctuationText": null,
                              "bigFluctuation": false,
                              "icon": null
                            } : null,
                          },
                        ],
                      }}
                    />
                  </Col>
                  <Col span={12}>
                    <Widget
                      data={{
                        background: "white",
                        title: t("Ideal Value"),
                        icon: null,
                        charts: [
                          {
                            chartType: "bigNumberChart",
                            chartId: "bigNumberChartOne",
                            height: "111px",
                            isFetching: confortHistoricDataIsFetching,
                            data: confortHistoricData ? {
                              "number": confortHistoricData[selectedMetrics][currentFloor.label].ideal,
                              "unit": confortHistoricData[selectedMetrics][currentFloor.label].unit,
                              "type": null,
                              "label": null,
                              "fluctuation": null,
                              "fluctuationText": null,
                              "bigFluctuation": false,
                              "icon": null
                            } : null,
                          },
                        ],
                      }}
                    />
                  </Col>
                  <Col span={24}>
                    <Widget
                      className="full-h"
                      data={{
                        background: "white",
                        title: t(metrics.find(x => x.value === selectedMetrics)?.label + " Variation"),
                        icon: null,
                        charts: [
                          {
                            chartType: "areaChartApex",
                            chartId: "areaChartApex",
                            height: "300px",
                            isFetching: confortHistoricDataIsFetching,
                            data: confortHistoricData ? serealizeAreaChartVariation(confortHistoricData[selectedMetrics][currentFloor.label], selectedMonth, t) : null,
                          },
                        ],
                      }}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
      </PageContent>
    </>
  );
};

const getAverageOfThatMonth = (data, selectedYear, selectedMonth) => {
  const byMonth = groupByMonthAndSumValues(data.values);
  return byMonth[`${selectedYear}/${selectedMonth}`] ?? 0;
}

const serealizeAreaChartVariation = (data, selectedMonth, t) => {
  const idealValue = data.ideal;

  const series = [
    {
      "name": t("Estimated Value"),
      "data": []
    },
    {
      "name": t("Real Value"),
      "data": []
    }
  ];

  const byMonth = groupByMonth(data.values);

  Object.entries(byMonth).map((x) => {
    const [key, value] = x;
    const m = key.split('/')[1];

    if (m != selectedMonth) return;

    value.map((v) => {
      series[0].data.push({
        "x": t(v.day),
        "y": idealValue
      });

      series[1].data.push({
        "x": t(v.day),
        "y": v.value
      });
    });
  });

  //Codigo para os meses todos no ano
  //const byMonth = groupByMonthAndSumValues(data.values);
  // Object.entries(byMonth).map((x) => {
  //   const [key, value] = x;
  //   const m = key.split('/')[1];

  //   series[0].data.push({
  //     "x": t(months[m - 1].label),
  //     "y": idealValue
  //   });

  //   series[1].data.push({
  //     "x": t(months[m - 1].label),
  //     "y": value
  //   });
  // });

  return {
    "series": series,
    "categories": null,
    "xAxisType": "number",
    "markerSize": 3,
    "columnWidth": 20,
    "colors": [
      Theme.color['temperature-real'],
      Theme.color['temperature-estimated']
    ],
    "unit": data.unit
  };
}

// Função para agrupar os valores por mês no ano
function groupByMonthAndSumValues(dados) {
  const resultado = {};

  for (const intervalo in dados) {
    const valor = dados[intervalo];
    const [inicio, _] = intervalo.split("-");
    const [ano, mes] = inicio.split("/").slice(0, 2);

    const chaveMes = `${ano}/${mes}`;
    if (!resultado[chaveMes]) {
      resultado[chaveMes] = 0;
    }

    resultado[chaveMes] += valor;
  }

  return resultado;
}

// Função para agrupar valores por mês
function groupByMonth(obj) {
  const agrupado = {};
  if (obj == null || obj == undefined)
    return agrupado;
  for (const [key, value] of Object.entries(obj)) {
    // Extrair o ano e o mês da chave (formato YYYY/M/D)
    const dataInicio = key.split(":")[0].split("/"); // ["2024", "1", "1"]
    const anoMes = `${dataInicio[0]}/${dataInicio[1]}`; // "2024/1"

    // Verificar se o mês já existe no objeto agrupado
    if (!agrupado[anoMes]) {
      agrupado[anoMes] = [];
    }

    // Adicionar o valor ao array correspondente ao mês
    agrupado[anoMes].push({ day: dataInicio[2] + '/' + dataInicio[1], value: value });
  }

  return agrupado;
}